// Widget Management Page - View, configure, and get embed codes for chat widgets

import { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardBody, Button, Modal } from '../components/common';
import { widgetService, type WidgetUpdateRequest } from '../services/widget';
import type { WidgetConfig, WidgetEmbedCode } from '../types/api';
import { formatDate } from '../utils/dateFormatters';
import {
  CodeIcon,
  CopyIcon,
  CheckIcon,
  RefreshIcon,
  GlobeIcon,
  SettingsIcon,
  PlayIcon,
  EditIcon,
  PlusIcon,
  TrashIcon,
} from '../components/icons';
import styles from './WidgetManagementPage.module.css';

export function WidgetManagementPage() {
  const [widgets, setWidgets] = useState<WidgetConfig[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Embed code modal state
  const [selectedWidget, setSelectedWidget] = useState<WidgetConfig | null>(null);
  const [embedCode, setEmbedCode] = useState<WidgetEmbedCode | null>(null);
  const [isLoadingEmbed, setIsLoadingEmbed] = useState(false);
  const [embedPosition, setEmbedPosition] = useState<'bottom-right' | 'bottom-left'>('bottom-right');
  const [copied, setCopied] = useState(false);

  // Toggle active state
  const [isTogglingActive, setIsTogglingActive] = useState<string | null>(null);

  // Edit modal state
  const [editingWidget, setEditingWidget] = useState<WidgetConfig | null>(null);
  const [editForm, setEditForm] = useState<WidgetUpdateRequest>({});
  const [isSaving, setIsSaving] = useState(false);
  const [newDomain, setNewDomain] = useState('');

  // Load widgets on mount
  useEffect(() => {
    loadWidgets();
  }, []);

  const loadWidgets = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await widgetService.getWidgets();
      setWidgets(data);
    } catch (err) {
      console.error('Failed to load widgets:', err);
      setError('Failed to load widgets. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleToggleActive = useCallback(async (widget: WidgetConfig) => {
    setIsTogglingActive(widget.id);
    setError(null);
    try {
      const updated = await widgetService.updateWidget(widget.id, { is_active: !widget.is_active });
      setWidgets((prev) => prev.map((w) => (w.id === widget.id ? updated : w)));
    } catch (err) {
      console.error('Failed to toggle widget status:', err);
      setError('Failed to update widget status. Please try again.');
    } finally {
      setIsTogglingActive(null);
    }
  }, []);

  const handleTestWidget = useCallback((widget: WidgetConfig) => {
    // Open the test page in a new tab
    window.open(`/widget-test/${widget.id}`, '_blank');
  }, []);

  const handleGetEmbedCode = useCallback(async (widget: WidgetConfig) => {
    setSelectedWidget(widget);
    setIsLoadingEmbed(true);
    setCopied(false);
    try {
      const code = await widgetService.getEmbedCode(widget.id, embedPosition);
      setEmbedCode(code);
    } catch (err) {
      console.error('Failed to get embed code:', err);
      setError('Failed to get embed code.');
    } finally {
      setIsLoadingEmbed(false);
    }
  }, [embedPosition]);

  const handlePositionChange = useCallback(async (position: 'bottom-right' | 'bottom-left') => {
    setEmbedPosition(position);
    if (selectedWidget) {
      setIsLoadingEmbed(true);
      setCopied(false);
      try {
        const code = await widgetService.getEmbedCode(selectedWidget.id, position);
        setEmbedCode(code);
      } catch (err) {
        console.error('Failed to get embed code:', err);
      } finally {
        setIsLoadingEmbed(false);
      }
    }
  }, [selectedWidget]);

  const handleCopyCode = useCallback(async () => {
    if (embedCode) {
      try {
        await navigator.clipboard.writeText(embedCode.embed_code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  }, [embedCode]);

  const handleCloseModal = useCallback(() => {
    setSelectedWidget(null);
    setEmbedCode(null);
    setCopied(false);
  }, []);

  const handleOpenEditModal = useCallback((widget: WidgetConfig) => {
    setEditingWidget(widget);
    setEditForm({
      name: widget.name,
      description: widget.description,
      allowed_domains: [...widget.allowed_domains],
      rate_limit_per_minute: widget.rate_limit_per_minute,
      max_concurrent_sessions: widget.max_concurrent_sessions,
      session_duration_hours: widget.session_duration_hours,
      conversation_retention_days: widget.conversation_retention_days,
      custom_greeting: widget.custom_greeting,
    });
    setNewDomain('');
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setEditingWidget(null);
    setEditForm({});
    setNewDomain('');
  }, []);

  const handleEditFormChange = useCallback((field: keyof WidgetUpdateRequest, value: unknown) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleAddDomain = useCallback(() => {
    const domain = newDomain.trim();
    if (domain && editForm.allowed_domains && !editForm.allowed_domains.includes(domain)) {
      setEditForm((prev) => ({
        ...prev,
        allowed_domains: [...(prev.allowed_domains || []), domain],
      }));
      setNewDomain('');
    }
  }, [newDomain, editForm.allowed_domains]);

  const handleRemoveDomain = useCallback((domain: string) => {
    setEditForm((prev) => ({
      ...prev,
      allowed_domains: (prev.allowed_domains || []).filter((d) => d !== domain),
    }));
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!editingWidget) return;

    setIsSaving(true);
    setError(null);
    try {
      const updated = await widgetService.updateWidget(editingWidget.id, editForm);
      setWidgets((prev) => prev.map((w) => (w.id === editingWidget.id ? updated : w)));
      handleCloseEditModal();
    } catch (err) {
      console.error('Failed to update widget:', err);
      setError('Failed to update widget settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [editingWidget, editForm, handleCloseEditModal]);

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerText}>
          <h1>Widget Management</h1>
          <p className={styles.subtitle}>
            Configure and embed chat widgets on your websites
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={loadWidgets}
          isLoading={isLoading}
          leftIcon={<RefreshIcon />}
        >
          Refresh
        </Button>
      </div>

      {/* Error display */}
      {error && (
        <div className={styles.error}>
          {error}
        </div>
      )}

      {/* Loading state */}
      {isLoading && widgets.length === 0 && (
        <div className={styles.loading}>
          <div className={styles.spinner} />
          <span>Loading widgets...</span>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && widgets.length === 0 && !error && (
        <Card>
          <CardBody>
            <div className={styles.emptyState}>
              <GlobeIcon />
              <h3>No widgets configured</h3>
              <p>Create a widget configuration to embed the chat on your website.</p>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Widget grid */}
      {widgets.length > 0 && (
        <div className={styles.grid}>
          {widgets.map((widget) => (
            <Card key={widget.id} className={!widget.is_active ? styles.inactiveCard : ''}>
              <CardHeader>
                <div className={styles.cardTitle}>
                  <h3>{widget.name}</h3>
                  <button
                    className={`${styles.statusToggle} ${widget.is_active ? styles.active : styles.inactive}`}
                    onClick={() => handleToggleActive(widget)}
                    disabled={isTogglingActive === widget.id}
                    title={widget.is_active ? 'Click to deactivate' : 'Click to activate'}
                  >
                    {isTogglingActive === widget.id ? '...' : widget.is_active ? 'Active' : 'Inactive'}
                  </button>
                </div>
              </CardHeader>
              <CardBody>
                {widget.description && (
                  <p className={styles.description}>{widget.description}</p>
                )}

                <div className={styles.details}>
                  <div className={styles.detailItem}>
                    <GlobeIcon />
                    <span className={styles.detailLabel}>Allowed Domains:</span>
                    <span className={styles.detailValue}>
                      {widget.allowed_domains.length > 0
                        ? widget.allowed_domains.slice(0, 3).join(', ')
                        : 'None configured'}
                      {widget.allowed_domains.length > 3 && ` +${widget.allowed_domains.length - 3} more`}
                    </span>
                  </div>

                  <div className={styles.detailItem}>
                    <SettingsIcon />
                    <span className={styles.detailLabel}>Settings:</span>
                    <span className={styles.detailValue}>
                      {widget.rate_limit_per_minute} req/min, {widget.max_concurrent_sessions} sessions, {widget.session_duration_hours}h duration
                    </span>
                  </div>

                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Created:</span>
                    <span className={styles.detailValue}>{formatDate(widget.created_at)}</span>
                  </div>
                </div>

                {widget.custom_greeting && (
                  <div className={styles.greeting}>
                    <strong>Greeting:</strong> "{widget.custom_greeting}"
                  </div>
                )}

                {/* Action buttons at bottom of card */}
                <div className={styles.cardActions}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenEditModal(widget)}
                    leftIcon={<EditIcon />}
                  >
                    Edit Settings
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleTestWidget(widget)}
                    leftIcon={<PlayIcon />}
                  >
                    Test Widget
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleGetEmbedCode(widget)}
                    leftIcon={<CodeIcon />}
                  >
                    Get Embed Code
                  </Button>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {/* Embed Code Modal */}
      <Modal
        isOpen={selectedWidget !== null}
        onClose={handleCloseModal}
        title={`Embed Code: ${selectedWidget?.name || ''}`}
        size="lg"
      >
        {selectedWidget && (
          <div className={styles.embedModal}>
            {/* Position selector */}
            <div className={styles.positionSelector}>
              <label>Widget Position:</label>
              <div className={styles.positionButtons}>
                <button
                  className={`${styles.positionButton} ${embedPosition === 'bottom-right' ? styles.selected : ''}`}
                  onClick={() => handlePositionChange('bottom-right')}
                >
                  Bottom Right
                </button>
                <button
                  className={`${styles.positionButton} ${embedPosition === 'bottom-left' ? styles.selected : ''}`}
                  onClick={() => handlePositionChange('bottom-left')}
                >
                  Bottom Left
                </button>
              </div>
            </div>

            {/* Allowed domains info */}
            <div className={styles.domainsInfo}>
              <strong>Allowed domains:</strong>
              <ul>
                {selectedWidget.allowed_domains.map((domain, i) => (
                  <li key={i}>{domain}</li>
                ))}
              </ul>
              <p className={styles.domainsNote}>
                The widget will only work on these domains. For testing from api.chemmasters.net,
                the wildcard *.chemmasters.net should allow it.
              </p>
            </div>

            {/* Code display */}
            <div className={styles.codeSection}>
              <div className={styles.codeHeader}>
                <span>HTML Embed Code</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyCode}
                  leftIcon={copied ? <CheckIcon /> : <CopyIcon />}
                  disabled={isLoadingEmbed}
                >
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              </div>
              <div className={styles.codeBlock}>
                {isLoadingEmbed ? (
                  <span className={styles.codeLoading}>Loading...</span>
                ) : (
                  <code>{embedCode?.embed_code || 'Error loading code'}</code>
                )}
              </div>
            </div>

            {/* Instructions */}
            <div className={styles.instructions}>
              <h4>How to use:</h4>
              <ol>
                <li>Copy the embed code above</li>
                <li>Paste it into your HTML, just before the closing <code>&lt;/body&gt;</code> tag</li>
                <li>The chat widget will appear in the {embedPosition.replace('-', ' ')} corner</li>
              </ol>
            </div>
          </div>
        )}
      </Modal>

      {/* Edit Settings Modal */}
      <Modal
        isOpen={editingWidget !== null}
        onClose={handleCloseEditModal}
        title={`Edit Settings: ${editingWidget?.name || ''}`}
        size="lg"
      >
        {editingWidget && (
          <div className={styles.editModal}>
            {/* Basic Info */}
            <div className={styles.formSection}>
              <h4 className={styles.formSectionTitle}>Basic Information</h4>

              <div className={styles.formGroup}>
                <label htmlFor="edit-name">Widget Name</label>
                <input
                  id="edit-name"
                  type="text"
                  value={editForm.name || ''}
                  onChange={(e) => handleEditFormChange('name', e.target.value)}
                  className={styles.formInput}
                  placeholder="Enter widget name"
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="edit-description">Description</label>
                <textarea
                  id="edit-description"
                  value={editForm.description || ''}
                  onChange={(e) => handleEditFormChange('description', e.target.value || null)}
                  className={styles.formTextarea}
                  placeholder="Enter description (optional)"
                  rows={2}
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="edit-greeting">Custom Greeting</label>
                <textarea
                  id="edit-greeting"
                  value={editForm.custom_greeting || ''}
                  onChange={(e) => handleEditFormChange('custom_greeting', e.target.value || null)}
                  className={styles.formTextarea}
                  placeholder="Enter custom greeting message (optional)"
                  rows={2}
                />
              </div>
            </div>

            {/* Allowed Domains */}
            <div className={styles.formSection}>
              <h4 className={styles.formSectionTitle}>Allowed Domains</h4>
              <p className={styles.formHint}>
                Domains where the widget can be embedded. Use * for wildcard (e.g., *.example.com)
              </p>

              <div className={styles.domainList}>
                {(editForm.allowed_domains || []).map((domain) => (
                  <div key={domain} className={styles.domainTag}>
                    <span>{domain}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveDomain(domain)}
                      className={styles.domainRemove}
                      title="Remove domain"
                    >
                      <TrashIcon />
                    </button>
                  </div>
                ))}
              </div>

              <div className={styles.domainInput}>
                <input
                  type="text"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddDomain();
                    }
                  }}
                  className={styles.formInput}
                  placeholder="example.com or *.example.com"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAddDomain}
                  leftIcon={<PlusIcon />}
                  disabled={!newDomain.trim()}
                >
                  Add
                </Button>
              </div>
            </div>

            {/* Rate Limits & Sessions */}
            <div className={styles.formSection}>
              <h4 className={styles.formSectionTitle}>Limits & Sessions</h4>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="edit-rate-limit">Rate Limit (req/min)</label>
                  <input
                    id="edit-rate-limit"
                    type="number"
                    min="1"
                    max="1000"
                    value={editForm.rate_limit_per_minute || 10}
                    onChange={(e) => handleEditFormChange('rate_limit_per_minute', parseInt(e.target.value) || 10)}
                    className={styles.formInput}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="edit-max-sessions">Max Concurrent Sessions</label>
                  <input
                    id="edit-max-sessions"
                    type="number"
                    min="1"
                    max="1000"
                    value={editForm.max_concurrent_sessions || 20}
                    onChange={(e) => handleEditFormChange('max_concurrent_sessions', parseInt(e.target.value) || 20)}
                    className={styles.formInput}
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="edit-session-duration">Session Duration (hours)</label>
                  <input
                    id="edit-session-duration"
                    type="number"
                    min="1"
                    max="168"
                    value={editForm.session_duration_hours || 1}
                    onChange={(e) => handleEditFormChange('session_duration_hours', parseInt(e.target.value) || 1)}
                    className={styles.formInput}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="edit-retention">Conversation Retention (days)</label>
                  <input
                    id="edit-retention"
                    type="number"
                    min="1"
                    max="365"
                    value={editForm.conversation_retention_days || 30}
                    onChange={(e) => handleEditFormChange('conversation_retention_days', parseInt(e.target.value) || 30)}
                    className={styles.formInput}
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className={styles.editModalActions}>
              <Button
                variant="outline"
                onClick={handleCloseEditModal}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSaveEdit}
                isLoading={isSaving}
              >
                Save Changes
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
